import { z } from 'zod';

// =============================================================================
//  CRM Lead schemas — Story 11.1 (fundação Fase 3)
//  Fonte única back+front (arquitetura 08-fase3-crm-architecture.md § III.2, FR-50).
//  Refinements de CRUD (dedup-warning, soft discard) ficam na story 11.2.
// =============================================================================

export const crmLeadStatusSchema = z.enum([
  'novo',
  'qualificando',
  'qualificado',
  'descartado',
  'convertido',
]);
export type CrmLeadStatusSchema = z.infer<typeof crmLeadStatusSchema>;

export const crmLeadOriginSchema = z.enum([
  'instagram',
  'whatsapp',
  'indicacao',
  'trafego_pago',
  'site',
  'landing_page',
  'evento',
  'prospeccao_ativa',
  'outro',
]);
export type CrmLeadOriginSchema = z.infer<typeof crmLeadOriginSchema>;

export const crmLeadTemperatureSchema = z.enum(['frio', 'morno', 'quente']);
export type CrmLeadTemperatureSchema = z.infer<typeof crmLeadTemperatureSchema>;

export const crmLeadPotentialSchema = z.enum(['low', 'medium', 'high']);
export type CrmLeadPotentialSchema = z.infer<typeof crmLeadPotentialSchema>;

// -----------------------------------------------------------------------------
// CREATE
// -----------------------------------------------------------------------------

export const createCrmLeadSchema = z.object({
  name: z.string().trim().min(1, 'Nome obrigatório.').max(200, 'Nome deve ter até 200 caracteres.'),
  company: z.string().max(200).optional().nullable(),
  segment: z.string().max(120).optional().nullable(),
  phone: z.string().max(20, 'Telefone deve ter até 20 caracteres.').optional().nullable(),
  email: z
    .union([z.string().email('Email inválido.'), z.literal('')])
    .optional()
    .nullable(),
  origin: crmLeadOriginSchema.default('outro'),
  saas_product_id: z.string().uuid('saas_product_id inválido.').optional().nullable(),
  pain_point: z.string().max(5000).optional().nullable(),
  potential: crmLeadPotentialSchema.default('medium'),
  temperature: crmLeadTemperatureSchema.default('frio'),
  responsible_user_id: z.string().uuid('responsible_user_id inválido.').optional().nullable(),
  next_action: z.string().max(500).optional().nullable(),
  next_contact_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD')
    .optional()
    .nullable(),
  client_id: z.string().uuid('client_id inválido.').optional().nullable(),
  observations: z.string().max(5000).optional().nullable(),
});
export type CreateCrmLeadDto = z.output<typeof createCrmLeadSchema>;
export type CreateCrmLeadInput = z.input<typeof createCrmLeadSchema>;

// -----------------------------------------------------------------------------
// UPDATE — todos os campos criáveis opcionais (PATCH semântico via PUT) +
// status/discard_reason. `version` obrigatório pra optimistic lock (DR-028, AC5).
// -----------------------------------------------------------------------------

export const updateCrmLeadSchema = createCrmLeadSchema
  .partial()
  .extend({
    status: crmLeadStatusSchema.optional(),
    discard_reason: z.string().max(500).optional().nullable(),
    version: z.coerce.number().int().positive('version é obrigatório (optimistic lock).'),
  })
  .strict();
export type UpdateCrmLeadDto = z.output<typeof updateCrmLeadSchema>;
export type UpdateCrmLeadInput = z.input<typeof updateCrmLeadSchema>;

// -----------------------------------------------------------------------------
// DISCARD — soft delete (FR-50.8). `discard_reason` obrigatório, min 5 chars (AC6).
// -----------------------------------------------------------------------------

export const discardCrmLeadSchema = z
  .object({
    discard_reason: z
      .string()
      .trim()
      .min(5, 'Motivo do descarte deve ter ao menos 5 caracteres.')
      .max(500, 'Motivo do descarte deve ter até 500 caracteres.'),
  })
  .strict();
export type DiscardCrmLeadDto = z.output<typeof discardCrmLeadSchema>;
export type DiscardCrmLeadInput = z.input<typeof discardCrmLeadSchema>;

// -----------------------------------------------------------------------------
// QUERY — cursor pagination (DR-032) + filtros FR-50 (AC3).
// `overdue_contact` = leads com next_contact_date < hoje (FR-50.6).
// `q` = busca textual em name/company/phone/email.
// -----------------------------------------------------------------------------

const booleanQuerySchema = z
  .union([z.boolean(), z.enum(['true', 'false', '1', '0'])])
  .transform((v) => v === true || v === 'true' || v === '1');

export const queryCrmLeadsSchema = z.object({
  status: crmLeadStatusSchema.optional(),
  temperature: crmLeadTemperatureSchema.optional(),
  origin: crmLeadOriginSchema.optional(),
  responsible_user_id: z.string().uuid('responsible_user_id inválido.').optional(),
  saas_product_id: z.string().uuid('saas_product_id inválido.').optional(),
  overdue_contact: booleanQuerySchema.optional(),
  q: z.string().trim().min(1).max(200).optional(),
  cursor: z.string().uuid('cursor inválido.').optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type QueryCrmLeadsDto = z.infer<typeof queryCrmLeadsSchema>;

// -----------------------------------------------------------------------------
// DEDUP-WARNING (FR-50.9 / AC2) — shape do aviso de duplicata retornado no POST.
// Avisa, não bloqueia. `match_field` indica qual campo casou.
// -----------------------------------------------------------------------------

export const crmLeadDuplicateWarningSchema = z.object({
  type: z.enum(['duplicate_lead', 'duplicate_client']),
  matched_id: z.string().uuid(),
  matched_name: z.string(),
  match_field: z.enum(['phone', 'email', 'document']),
});
export type CrmLeadDuplicateWarning = z.infer<typeof crmLeadDuplicateWarningSchema>;
